// ---------------------------------------------------------------------------
// exportDialog.js — export options dialog (spec H).
//
// Format (PNG / JPEG / WebP), quality slider for lossy formats, size multiplier
// (0.5x / 1x / 2x), and a background choice (transparent vs flatten-on-color).
// Keeps BOTH the primary "Foloseste" (hands dataURL + designJson back to the
// host — calendar EventForm / KFileDrop / products) and a "Descarca" download.
//
// Rasterization reuses the FOUNDATION's proven export path via the seam methods
// `editor.exportImage(format, multiplier)` (identity viewport, artboard-rect crop,
// pasteboard/outline skipped) and `editor.emitExport(dataURL)` (calls the host's
// onExport with the current designJson). We take a transparent (or artboard-bg)
// PNG from the foundation and post-process format / quality / flatten locally —
// no viewport/coordinate math here, no re-render of the design.
//
// Registers: 1 tool button ("Exporta") + 1 overlay (the dialog).
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { registerOverlay } from './overlayHost';

const FORMATS = [
  { id: 'png', label: 'PNG', mime: 'image/png', lossy: false },
  { id: 'jpeg', label: 'JPEG', mime: 'image/jpeg', lossy: true },
  { id: 'webp', label: 'WebP', mime: 'image/webp', lossy: true },
];
const MULTIPLIERS = [
  { v: 0.5, label: '0.5x' },
  { v: 1, label: '1x' },
  { v: 2, label: '2x' },
];

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function ExportDialog({ editor }) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(0.92);
  const [multiplier, setMultiplier] = useState(1);
  const [background, setBackground] = useState('transparent'); // 'transparent' | 'white' | 'artboard'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const baseImgRef = useRef(null);   // cached transparent/artboard base PNG @1x
  const artboardBgRef = useRef(null);
  const previewTimer = useRef(null);

  const fmt = FORMATS.find((f) => f.id === format) || FORMATS[0];
  const artboardBg = artboardBgRef.current;

  const resolveBg = useCallback(() => {
    // JPEG cannot store alpha — force a flat color.
    if (fmt.id === 'jpeg' && background === 'transparent') return '#ffffff';
    if (background === 'transparent') return null;
    if (background === 'white') return '#ffffff';
    if (background === 'artboard') return editor.getArtboardBg() || '#ffffff';
    return null;
  }, [fmt.id, background, editor]);

  // Composite the cached (or freshly exported) base onto the chosen background and
  // encode in the chosen format/quality. `mult === 1` reuses the cached base.
  const buildDataURL = useCallback(async (mult) => {
    let baseImg = baseImgRef.current;
    if (mult !== 1 || !baseImg) {
      const basePng = editor.exportImage('png', mult);
      baseImg = await loadImage(basePng);
      if (mult === 1) baseImgRef.current = baseImg;
    }
    const out = document.createElement('canvas');
    out.width = baseImg.naturalWidth || baseImg.width;
    out.height = baseImg.naturalHeight || baseImg.height;
    const ctx = out.getContext('2d');
    const bg = resolveBg();
    if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, out.width, out.height); }
    ctx.drawImage(baseImg, 0, 0);
    return out.toDataURL(fmt.mime, fmt.lossy ? quality : undefined);
  }, [editor, resolveBg, fmt.mime, fmt.lossy, quality]);

  // Open: snapshot the base once (single foundation export), set sensible bg.
  useEffect(() => {
    editor.overlays.export = () => {
      baseImgRef.current = null;
      artboardBgRef.current = editor.getArtboardBg() || null;
      setFormat('png'); setQuality(0.92); setMultiplier(1);
      setBackground(artboardBgRef.current ? 'artboard' : 'transparent');
      setPreviewUrl(null);
      setOpen(true);
    };
    return () => { if (editor.overlays) delete editor.overlays.export; };
  }, [editor]);

  // Keep the preview in sync (debounced) at 1x — the visual honors format/quality/bg.
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try { setPreviewUrl(await buildDataURL(1)); } catch (e) { /* preview best-effort */ }
    }, 220);
    return () => clearTimeout(previewTimer.current);
  }, [open, format, quality, background, buildDataURL]);

  if (!open) return null;

  const close = () => setOpen(false);

  const pickFormat = (id) => {
    setFormat(id);
    // JPEG has no alpha — move off "transparent" so the choice is truthful.
    if (id === 'jpeg' && background === 'transparent') setBackground(artboardBg ? 'artboard' : 'white');
  };

  const doDownload = async () => {
    setBusy(true);
    try {
      const url = await buildDataURL(multiplier);
      const a = document.createElement('a');
      a.href = url;
      a.download = `design.${fmt.id === 'jpeg' ? 'jpg' : fmt.id}`;
      a.click();
    } catch (e) {
      editor.toast('Descarcarea a esuat. O imagine din editor nu permite exportul (CORS).', 'error');
    } finally { setBusy(false); }
  };

  const doUse = async () => {
    setBusy(true);
    try {
      const url = await buildDataURL(multiplier);
      editor.emitExport(url); // hands dataURL + designJson to the host (and closes the modal)
    } catch (e) {
      setBusy(false);
      editor.toast('Exportul a esuat. O imagine din editor nu permite exportul (CORS).', 'error');
    }
  };

  const { width: W, height: H } = editor.getArtboard();
  const outW = Math.round(W * multiplier);
  const outH = Math.round(H * multiplier);
  // When the artboard has a solid bg the exported base is already opaque with it,
  // so transparent/white can't be honored — only "Culoare panza" is meaningful.
  const transparentDisabled = !!artboardBg || fmt.id === 'jpeg';
  const whiteDisabled = !!artboardBg;

  return (
    <div className="de-ov-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="de-ov-dialog de-export-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="de-ov-header">
          <h3>Exporta imaginea</h3>
          <button className="de-ov-close" onClick={close} title="Inchide">&#10005;</button>
        </div>
        <div className="de-ov-body">
          <div className="de-export-grid">
            <div>
              <div className="de-export-field">
                <label>Format</label>
                <div className="de-chip-row">
                  {FORMATS.map((f) => (
                    <button key={f.id} className={`de-chip ${format === f.id ? 'active' : ''}`} onClick={() => pickFormat(f.id)}>{f.label}</button>
                  ))}
                </div>
              </div>

              {fmt.lossy && (
                <div className="de-export-field">
                  <label>Calitate ({Math.round(quality * 100)}%)</label>
                  <input type="range" min="0.3" max="1" step="0.01" value={quality} onChange={(e) => setQuality(parseFloat(e.target.value))} className="de-range" style={{ width: '100%' }} />
                </div>
              )}

              <div className="de-export-field">
                <label>Dimensiune</label>
                <div className="de-chip-row">
                  {MULTIPLIERS.map((m) => (
                    <button key={m.v} className={`de-chip ${multiplier === m.v ? 'active' : ''}`} onClick={() => setMultiplier(m.v)}>{m.label}</button>
                  ))}
                </div>
              </div>

              <div className="de-export-field">
                <label>Fundal</label>
                <div className="de-chip-row">
                  <button className={`de-chip ${background === 'transparent' && !transparentDisabled ? 'active' : ''}`} onClick={() => setBackground('transparent')} disabled={transparentDisabled} title={transparentDisabled ? 'Indisponibil pentru JPEG sau cand panza are fundal' : 'Fundal transparent'}>Transparent</button>
                  <button className={`de-chip ${background === 'white' && !whiteDisabled ? 'active' : ''}`} onClick={() => setBackground('white')} disabled={whiteDisabled} title={whiteDisabled ? 'Panza are deja un fundal setat' : 'Fundal alb'}>Alb</button>
                  {artboardBg && (
                    <button className={`de-chip ${background === 'artboard' ? 'active' : ''}`} onClick={() => setBackground('artboard')}>Culoare panza</button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', marginBottom: 6, fontWeight: 600 }}>Previzualizare</label>
              <div className="de-export-preview">
                {previewUrl ? <img src={previewUrl} alt="Previzualizare export" /> : <span style={{ color: '#888' }}>Se pregateste...</span>}
              </div>
              <div className="de-export-meta">{fmt.label} &bull; {outW} &times; {outH} px</div>
            </div>
          </div>
        </div>
        <div className="de-ov-footer">
          <button className="de-btn" onClick={close} disabled={busy}>Renunta</button>
          <button className="de-btn" onClick={doDownload} disabled={busy}>{busy ? 'Se exporta...' : 'Descarca'}</button>
          <button className="de-btn de-btn-primary" onClick={doUse} disabled={busy}>Foloseste</button>
        </div>
      </div>
    </div>
  );
}

export function registerExportDialog(editor) {
  registerOverlay(editor, 'export', ExportDialog);
  editor.addToolButton({
    id: 'de-export',
    title: 'Optiuni export (PNG / JPEG / WebP, calitate, dimensiune)',
    icon: 'Exporta',
    onClick: (ed) => { if (ed.overlays && ed.overlays.export) ed.overlays.export(); },
  });
}
